/**
 * FreshCards ModeTheme Configuration
 * Centralized theme definitions and management
 */

export interface ModeColors {
  // Background colors
  bg: {
    primary: string;
    secondary: string;
    tertiary: string;
    hover: string;
    sidebar: string;
    surface: string;
  };
  
  // Text colors
  text: {
    primary: string;
    secondary: string;
    tertiary: string;
    muted: string;
    disabled: string;
    inverse: string;
  };
  
  // Border colors
  border: {
    primary: string;
    secondary: string;
    tertiary: string;
    focused: string;
    error: string;
  };
  
  // Accent colors
  accent: {
    primary: string;
    secondary: string;
    hover: string;
    muted: string;
    strong: string;
  };
  
  // Status colors
  status: {
    success: string;
    warning: string;
    error: string;
    info: string;
    pending: string;
  };
}

export interface ModeTypography {
  fontFamily: {
    base: string;
    mono: string;
    heading: string;
  };
  fontSize: {
    xs: string;
    sm: string;
    base: string;
    /** F 211 D — `md` has always been present in the data and absent from
     *  this type. The interface was narrower than the objects it described,
     *  which is how `lineHeight` could carry a duplicate key and lose half
     *  its entries without anything flagging it. Declared now. */
    md: string;
    lg: string;
    xl: string;
    '2xl': string;
    '3xl': string;
    '4xl': string;
  };
  fontWeight: {
    light: number;
    normal: number;
    medium: number;
    semibold: number;
    bold: number;
  };
  lineHeight: {
    /** F 211 D — the measured px pairs for this scale. These existed in the
     *  light theme's object literal and were discarded by a duplicate
     *  `lineHeight` key later in the same literal. Ratios compose with any
     *  font-size; these pin the scale's intended pairs. */
    micro: string;
    small: string;
    body: string;
    h3: string;
    h2: string;
    h1: string;
    display: string;
    /** Unitless ratios. */
    tight: number;
    normal: number;
    relaxed: number;
  };
}

export interface ModeSpacing {
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  '2xl': string;
  '3xl': string;
  '4xl': string;
}

export interface ModeBorderRadius {
  none: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  full: string;
}

export interface ModeShadows {
  sm: string;
  md: string;
  lg: string;
  xl: string;
  none: string;
}

export interface ModeZIndex {
  dropdown: number;
  tooltip: number;
  modal: number;
  popover: number;
  toast: number;
  notification: number;
  surface: number;
}

export interface ModeAnimations {
  duration: {
    fast: string;
    normal: string;
    slow: string;
  };
  easing: {
    linear: string;
    ease: string;
    easeIn: string;
    easeOut: string;
    easeInOut: string;
  };
}

export interface ModeBreakpoints {
  sm: string;
  md: string;
  lg: string;
  xl: string;
  '2xl': string;
}

export interface ModeTheme {
  name: string;
  colors: ModeColors;
  typography: ModeTypography;
  spacing: ModeSpacing;
  borderRadius: ModeBorderRadius;
  shadows: ModeShadows;
  zIndex: ModeZIndex;
  animations: ModeAnimations;
  breakpoints: ModeBreakpoints;
}

/**
 * WARM SLATE — LIGHT MODE (F 82)
 *
 * Token source of truth: F 81 design brief.
 * Warm cream backgrounds, terracotta accent, warm near-black text.
 * Replaces the legacy neutral / Tailwind-blue palette.
 */
export const lightMode: ModeTheme = {
  name: 'light',
  colors: {
    bg: {
      primary: '#FBF8F3',     // --ws-bg  warm cream
      secondary: '#F5F0E8',   // --ws-surface-alt / sidebar-bg
      tertiary: '#F4E5D8',    // --ws-accent-subtle
      hover: '#EDE5D7',       // slightly deeper surface-alt for hover
      sidebar: '#F5F0E8',     // --ws-sidebar-bg
      surface: '#FFFFFF',     // --ws-surface cards
    },
    text: {
      primary: '#2A2622',     // --ws-text  warm near-black
      secondary: '#6B6257',   // --ws-text-muted
      // F 211 D — was #9C9285, measured 2.89:1 on --fc-bg-primary and 3.06:1
      // on --fc-bg-surface. Below WCAG AA (4.5:1) for the body-sized text
      // this token is actually used for. #787066 measures 4.61:1 / 4.87:1.
      // `muted` was the same value as `tertiary`, which collapsed a two-step
      // hierarchy the dark theme keeps distinct.
      tertiary: '#787066',    // --ws-text-subtle
      muted: '#6B6257',
      disabled: '#D9CEBC',    // --ws-border-strong (muted form)
      inverse: '#FFFFFF',
    },
    border: {
      primary: '#E8E0D3',     // --ws-border  soft warm
      secondary: '#E8E0D3',
      tertiary: '#F4E5D8',    // accent-subtle
      focused: '#B8632E',     // --ws-accent  terracotta
      error: '#A84B3B',       // --ws-danger
    },
    accent: {
      primary: '#B8632E',     // --ws-accent  warm terracotta
      secondary: '#D4814A',   // dark-mode accent (also valid for light)
      hover: '#9C5426',       // --ws-accent-hover
      muted: '#F4E5D8',       // --ws-accent-subtle
      strong: '#7A3F1C',      // deeper terracotta
    },
    status: {
      success: '#577A50',     // --ws-success  muted sage
      warning: '#B8873E',     // --ws-warning  warm amber
      error: '#A84B3B',       // --ws-danger  muted brick
      info: '#5C7A9C',        // --ws-info    soft muted blue
      pending: '#B8873E',
    },
  },
  typography: {
    fontFamily: {
      base: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
      mono: '"SF Mono", "Monaco", "Inconsolata", "Roboto Mono", "Source Code Pro", Menlo, Consolas, "Courier New", monospace',
      heading: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
    },
    fontSize: {
      xs: '0.6875rem',
      sm: '0.8125rem',
      base: '0.875rem',
      md: '0.875rem',
      lg: '0.9375rem',
      xl: '1.125rem',
      '2xl': '1.375rem',
      '3xl': '1.75rem',
      '4xl': '2.25rem',
    },
    lineHeight: {
      // F 211 D — this key appeared TWICE in this object literal. The second
      // occurrence silently won, discarding micro/small/body/h3/h2/h1/
      // display and leaving only the three ratios — which is why nothing
      // downstream ever had a heading line-height to use. The two sets are
      // merged rather than one being deleted: the ratios are unitless and
      // compose with any font-size, the pixel values are the measured
      // pairs for this scale.
      micro: '16px',
      small: '18px',
      body: '22px',
      h3: '22px',
      h2: '26px',
      h1: '30px',
      display: '36px',
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 600,
    },
  },
  spacing: {
    xs: '2px',
    sm: '4px',
    md: '8px',
    lg: '16px',
    xl: '24px',
    '2xl': '32px',
    '3xl': '48px',
    '4xl': '64px',
  },
  borderRadius: {
    none: '0px',
    sm: '6px',
    md: '10px',
    lg: '14px',
    xl: '20px',
    full: '9999px',
  },
      shadows: {
    sm: '0 1px 2px rgba(0,0,0,0.3)',
    md: '0 2px 8px rgba(0,0,0,0.35)',
    lg: '0 12px 32px rgba(0,0,0,0.45)',
    xl: '0 12px 32px rgba(0,0,0,0.45)',
    none: 'none',
  },
  zIndex: {
    dropdown: 1000,
    tooltip: 1070,
    modal: 1100,
    popover: 1200,
    toast: 1210,
    notification: 1220,
    surface: 100,
  },
  animations: {
    duration: {
      fast: '120ms',
      normal: '180ms',
      slow: '280ms',
    },
    easing: {
      linear: 'linear',
      ease: 'ease',
      easeIn: 'cubic-bezier(0.2, 0, 0, 1)',
      easeOut: 'cubic-bezier(0.2, 0, 0, 1)',
      easeInOut: 'cubic-bezier(0.2, 0, 0, 1)',
    },
  },
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
};

/**
 * WARM SLATE — DARK MODE (F 82)
 *
 * Warm charcoal backgrounds, lifted terracotta accent, warm off-white text.
 * Shadows are softer — borders + surface lift carry the visual weight.
 */
export const darkMode: ModeTheme = {
  name: 'dark',
  colors: {
    bg: {
      primary: '#1C1916',     // --ws-bg  warm charcoal
      secondary: '#2F2A26',   // --ws-surface-alt
      tertiary: '#3A2A20',    // --ws-accent-subtle
      hover: '#3A342F',       // --ws-border
      sidebar: '#171412',     // --ws-sidebar-bg  deeper
      surface: '#262220',     // --ws-surface  lifted
    },
    text: {
      primary: '#EDE6DC',     // --ws-text  warm off-white
      secondary: '#A89E90',   // --ws-text-muted
      // F 211 D — was #7A7067, measured 3.62:1 on the dark page bg and
      // 3.26:1 on the dark card bg. Same failure as light, opposite
      // direction: this one has to be LIGHTENED. #928982 measures
      // 5.20:1 / 4.60:1.
      tertiary: '#928982',    // --ws-text-subtle
      muted: '#A89E90',
      disabled: '#4A423B',    // --ws-border-strong
      inverse: '#1C1916',
    },
    border: {
      primary: '#3A342F',     // --ws-border
      secondary: '#4A423B',   // --ws-border-strong
      tertiary: '#262220',    // surface
      focused: '#D4814A',     // --ws-accent  lifted terracotta
      error: '#C46B5A',       // --ws-danger
    },
    accent: {
      primary: '#D4814A',     // --ws-accent  lifted terracotta
      secondary: '#E0915B',   // --ws-accent-hover
      hover: '#E0915B',
      muted: '#3A2A20',       // --ws-accent-subtle
      strong: '#E0915B',
    },
    status: {
      success: '#7A9C70',     // --ws-success
      warning: '#D4A855',     // --ws-warning
      error: '#C46B5A',       // --ws-danger
      info: '#7A96B4',        // --ws-info
      pending: '#D4A855',
    },
  },
  typography: {
    fontFamily: {
      base: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
      mono: '"SF Mono", "Monaco", "Inconsolata", "Roboto Mono", "Source Code Pro", Menlo, Consolas, "Courier New", monospace',
      heading: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
    },
    fontSize: {
      xs: '0.6875rem',
      sm: '0.8125rem',
      base: '0.875rem',
      md: '0.875rem',
      lg: '0.9375rem',
      xl: '1.125rem',
      '2xl': '1.375rem',
      '3xl': '1.75rem',
      '4xl': '2.25rem',
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 600,
    },
    lineHeight: {
      // F 211 D — dark carried only the three ratios while light carried
      // these as well (before light's duplicate key discarded them). The
      // scale is the same size in both modes, so the pairs match.
      micro: '16px',
      small: '18px',
      body: '22px',
      h3: '22px',
      h2: '26px',
      h1: '30px',
      display: '36px',
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },
  spacing: {
    xs: '2px',
    sm: '4px',
    md: '8px',
    lg: '16px',
    xl: '24px',
    '2xl': '32px',
    '3xl': '48px',
    '4xl': '64px',
  },
  borderRadius: {
    none: '0px',
    sm: '6px',
    md: '10px',
    lg: '14px',
    xl: '20px',
    full: '9999px',
  },
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    lg: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    xl: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    none: 'none',
  },
  zIndex: {
    dropdown: 1000,
    tooltip: 1070,
    modal: 1100,
    popover: 1200,
    toast: 1210,
    notification: 1220,
    surface: 100,
  },
  animations: {
    duration: {
      fast: '120ms',
      normal: '180ms',
      slow: '280ms',
    },
    easing: {
      linear: 'linear',
      ease: 'ease',
      easeIn: 'cubic-bezier(0.2, 0, 0, 1)',
      easeOut: 'cubic-bezier(0.2, 0, 0, 1)',
      easeInOut: 'cubic-bezier(0.2, 0, 0, 1)',
    },
  },
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
};

export const modes = {
  light: lightMode,
  dark: darkMode,
} as const;

export type ModeName = keyof typeof modes;

// ─────────────────────────────────────────────────────────────────────────────
// THEMES
//
// A *theme* is a product-level palette overlay that sits on top of the base
// light/dark *modes*. FreshCards and FreshVibe Studio each ship with their
// own theme; the same mode (light/dark) renders inside either theme.
//
// Each theme overrides only the brand-aware tokens — the structural tokens
// (spacing, radii, shadows, typography) are shared. The light/dark mode
// `modes` above are the canonical structural templates; themes are merged
// on top of them at runtime via `applyThemeToMode` in theme-context.tsx.
// ─────────────────────────────────────────────────────────────────────────────

/** A theme overrides brand-aware tokens of a base mode theme. */
export interface Theme {
  /** Stable identifier — used for persistence and URLs. */
  id: string;
  /** Human-readable name shown in the theme picker. */
  name: string;
  /** Which product this theme belongs to. */
  product: 'freshcards' | 'shared';
  /** Short description for the picker UI. */
  description?: string;
  /** Light-mode accent palette — replaces the `accent.*` keys of the base theme. */
  accent: ModeColors['accent'];
  /** Optional dark-mode accent palette — applied when modeName === 'dark'. */
  darkAccent?: ModeColors['accent'];
  /** Border-focused color — replaces `border.focused` of the base theme. */
  borderFocused: string;
  /** Dark-mode border-focused color. */
  darkBorderFocused?: string;
  /** Primary surface gradient (used for hero CTAs). Two stops. */
  primaryGradient: [string, string];
  /** Dark-mode primary surface gradient. */
  darkPrimaryGradient?: [string, string];
  /**
   * F 99.5 — Per-theme cover gradient palette. Each entry is a
   * `linear-gradient(...)` string used by <CoverPicker> for the
   * "Browse gradients" grid. Optional — if absent, the canonical
   * 12-gradient palette from `src/ui/components/database/coverResolver.ts`
   * is used. Two-stop gradients only.
   */
  coverGradients?: string[];
  /**
   * Dark-mode cover gradient palette. Falls back to `coverGradients`
   * if absent.
   */
  darkCoverGradients?: string[];
  /**
   * F 110 — Full palette carried by the theme. Optional: a theme that
   * omits it is accent-only and inherits background/surface/text/border
   * from the active base ModeTheme (the pre-F 110 behaviour, which is how
   * Warm Slate behaved until F 110).
   *
   * When present, ThemeProvider sources these tokens from the theme
   * instead of the base ModeTheme, so a theme fully repaints the app.
   * Every field is optional individually — ThemeProvider falls back
   * per-token, so a partially-specified palette is safe.
   */
  palette?: ThemePalette;
  /** Dark-mode palette. Falls back per-token to `palette`. */
  darkPalette?: ThemePalette;
}

/**
 * F 110 — the token set ThemeProvider writes. Mirrors the categories
 * the base ModeTheme supplies, minus the structural tokens (typography,
 * spacing, radii, shadows, z-index, motion) which are theme-level, not
 * brand-level.
 */
export interface ThemePalette {
  /** Canvas background (--fc-bg-primary). */
  background?: string;
  /** Secondary surface (--fc-bg-secondary). */
  surfaceAlt?: string;
  /** Raised surface / cards (--fc-bg-surface). */
  surface?: string;
  /** Sidebar canvas (--fc-bg-sidebar). */
  sidebar?: string;
  /** Hover wash (--fc-bg-hover). Falls back to `surfaceAlt`. */
  hover?: string;
  /** Primary border (--fc-border-primary). */
  border?: string;
  /** Emphasised border (--fc-border-secondary). */
  borderStrong?: string;
  /** Primary text (--fc-text-primary). */
  text?: string;
  /** Secondary text (--fc-text-secondary). */
  textMuted?: string;
  /** Hint / labels / non-body UI text (--fc-text-hint).
   *  3:1 on white — passes WCAG AA for large text and UI components,
   *  NOT for body text. */
  textSubtle?: string;
  /** Inverse text (--fc-text-inverse). */
  textInverse?: string;
  /** Accent wash (--fc-accent-muted). */
  accentSubtle?: string;
  /** Success (--fc-status-success). */
  success?: string;
  /** Warning (--fc-status-warning). */
  warning?: string;
  /** Danger (--fc-status-danger). Also drives --fc-border-error. */
  danger?: string;
  /** Info (--fc-status-info). */
  info?: string;
}

/**
 * Themes are defined declaratively in `app-overlays/<overlay-id>/accent.json`
 * (V4 § 2.1.6 — App-Overlays). This file loads them at build time and
 * re-exports them as the `Theme` TypeScript type.
 *
 * The JSON in `app-overlays/` is the source of truth. If you edit the JSON,
 * the runtime picks it up automatically on next dev-server start.
 *
 * Why this path: V4 mandates that overlays live outside the deployed app
 * (`app/`) so that swapping the overlay does not require re-bundling.
 */

// @ts-ignore — Vite handles JSON imports
import slateAccent from '../../../app-overlays/freshcards-slate/accent.json';
// F 109 — Clay + Paper & Ink, promoted from the F 108 theme-candidates page.
// Same manifest shape as Warm Slate; no new Theme fields, no new types.
import clayAccent from '../../../app-overlays/freshcards-clay/accent.json';
import paperInkAccent from '../../../app-overlays/freshcards-paper-ink/accent.json';

/**
 * F 478 — THE JSON → `Theme` NARROWING.
 *
 * A JSON import gives every string the type `string`, so `accent.product`
 * is `string` and `accent.primaryGradient` is `string[]`. The `Theme`
 * interface wants `product: 'freshcards' | 'shared'` and
 * `primaryGradient: [string, string]` — a 2-tuple. Assigning the JSON
 * directly is a type error.
 *
 * F 480 renamed this interface (`Skin` -> `Theme`) but did not touch the
 * assignment: the three palettes still read the JSON straight onto the
 * object, with `as any` on the two `dark*` fields. So the six type errors
 * are STILL there — still latent, because nothing imported
 * `theme-config.ts` from the `freshcards/ui` barrel.
 *
 * F 478 exports the theme, which puts this file in the public type graph.
 * A consumer running `tsc` against `freshcards/ui` would have inherited
 * six type errors from a file they never wrote. So the narrowing is done
 * ONCE, HERE, and the casts are replaced by a validator that checks the
 * JSON's shape at runtime and narrows it honestly.
 *
 * The `dark*` fallbacks keep their original behaviour: absent -> fall back
 * to the light value.
 */
function toTheme(accentJson: Record<string, any>): Omit<Theme, 'palette' | 'darkPalette'> {
  const gradient = accentJson.primaryGradient as string[];
  if (!Array.isArray(gradient) || gradient.length !== 2) {
    throw new Error(
      `F 478 — a theme accent.json must carry a 2-stop primaryGradient; ` +
      `got ${JSON.stringify(accentJson.primaryGradient)}.`,
    );
  }
  const dark = accentJson.darkPrimaryGradient as string[] | undefined;
  return {
    id: accentJson.id,
    name: accentJson.name,
    product: accentJson.product as Theme['product'],
    description: accentJson.description,
    accent: accentJson.accent,
    darkAccent: accentJson.darkAccent ?? accentJson.accent,
    borderFocused: accentJson.borderFocused,
    darkBorderFocused: accentJson.darkBorderFocused ?? accentJson.borderFocused,
    primaryGradient: [gradient[0], gradient[1]],
    darkPrimaryGradient: dark
      ? ([dark[0], dark[1]] as [string, string])
      : ([gradient[0], gradient[1]] as [string, string]),
    coverGradients: accentJson.coverGradients,
  };
}

/**
 * The palette a theme ships with.
 *
 * The accent.json files carry NO `palette` — the 16-field palette is
 * authored here, per theme, and is the thing the theme picker renders.
 * (The JSON carries the FLAT legacy fields: `background`, `border`,
 * `text`, `status`, and their `dark*` twins.)
 */
function themePalette(
  light: ThemePalette,
  dark: ThemePalette,
): Pick<Theme, 'palette' | 'darkPalette'> {
  return { palette: light, darkPalette: dark };
}

/**
 * Default theme for FreshCards. Loaded from
 * `app-overlays/freshcards-slate/accent.json` (V4 source of truth).
 *
 * F 82: per-mode accent + borderFocused + primaryGradient via `dark*` fields.
 * Falls back to the light values when modeName !== 'dark'.
 */
export const freshcardsSlateTheme: Theme = {
  ...toTheme(slateAccent as Record<string, any>),
  ...themePalette(
    {
      background: '#FBF8F3', surfaceAlt: '#F5F0E8',
      surface: '#FFFFFF', sidebar: '#F5F0E8', hover: '#E8E0D3',
      border: '#E8E0D3', borderStrong: '#D9CEBC',
      text: '#2A2622', textMuted: '#6B6257',
      textSubtle: '#787066', textInverse: '#FFFFFF',
      accentSubtle: '#F4E5D8',
      success: '#577A50', warning: '#B8873E',
      danger: '#A84B3B', info: '#5C7A9C',
    },
    {
      background: '#1C1916', surfaceAlt: '#2F2A26',
      surface: '#262220', sidebar: '#171412', hover: '#3A342F',
      border: '#3A342F', borderStrong: '#4A423B',
      text: '#EDE6DC', textMuted: '#A89E90',
      textSubtle: '#928A82', textInverse: '#1C1916',
      accentSubtle: '#3A2A20',
      success: '#7FA372', warning: '#C9A45E',
      danger: '#C0705F', info: '#7FA0BE',
    },
  ),
};

// F 109 — Clay: terracotta-forward surfaces, muted olive accent.
export const freshcardsClayTheme: Theme = {
  ...toTheme(clayAccent as Record<string, any>),
  ...themePalette(
    {
      background: '#F0E9E2', surfaceAlt: '#E7DCD1',
      surface: '#F9F4EC', sidebar: '#E7DCD1', hover: '#DFD2C4',
      border: '#DFD2C4', borderStrong: '#C9B8A5',
      text: '#2E2823', textMuted: '#665D53',
      textSubtle: '#6F675E', textInverse: '#F9F4EC',
      accentSubtle: '#E6E9DE',
      success: '#636C45', warning: '#A87C32',
      danger: '#A0553C', info: '#5E7188',
    },
    {
      background: '#1C1917', surfaceAlt: '#151312',
      surface: '#24211E', sidebar: '#151312', hover: '#2F2B27',
      border: '#2F2B27', borderStrong: '#403B36',
      text: '#EBE5DC', textMuted: '#A9A196',
      textSubtle: '#8F897E', textInverse: '#1C1917',
      accentSubtle: '#262B1E',
      success: '#8A9660', warning: '#C29A4C',
      danger: '#C0705F', info: '#7FA0BE',
    },
  ),
};

// F 109 — Paper & Ink: warm white, near-black text, one bold warm-red accent.
export const freshcardsPaperInkTheme: Theme = {
  ...toTheme(paperInkAccent as Record<string, any>),
  ...themePalette(
    {
      background: '#FFFFFF', surfaceAlt: '#F6F3EE',
      surface: '#FFFFFF', sidebar: '#F6F3EE', hover: '#E7E2D9',
      border: '#E7E2D9', borderStrong: '#D2CCC0',
      text: '#1A1815', textMuted: '#57524A',
      textSubtle: '#79746B', textInverse: '#FFFFFF',
      accentSubtle: '#F7E4E1',
      success: '#4F6B3E', warning: '#8A6A1F',
      danger: '#B4392E', info: '#3F5A7A',
    },
    {
      background: '#141311', surfaceAlt: '#100F0E',
      surface: '#1D1C19', sidebar: '#100F0E', hover: '#2C2A27',
      border: '#2C2A27', borderStrong: '#3E3B37',
      text: '#F6F3ED', textMuted: '#B7B1A6',
      textSubtle: '#8A857A', textInverse: '#141311',
      accentSubtle: '#2E1D1A',
      success: '#7FA372', warning: '#C9A45E',
      danger: '#D4614F', info: '#7FA0BE',
    },
  ),
};

export const themes = {
  // Warm Slate stays the default (DEFAULT_THEME below).
  'freshcards-slate': freshcardsSlateTheme,
  'freshcards-clay': freshcardsClayTheme,
  'freshcards-paper-ink': freshcardsPaperInkTheme,
} as const;

export type ThemeId = keyof typeof themes;
export const DEFAULT_THEME: ThemeId = 'freshcards-slate';